import type { Filter } from '@repo/api-codegen';

/**
 * Recursively searches a filter tree for a sentiment filter and extracts its value.
 */
export function extractSentimentFromFilter(
	filter: Filter | null | undefined
): string | undefined {
	if (!filter) return undefined;

	if (filter.field === 'sentiment') {
		const { value } = filter;
		if (Array.isArray(value) && value.length > 0) {
			return value[0] as string;
		} else if (typeof value === 'string') {
			return value;
		}
	}

	if (filter.operands && Array.isArray(filter.operands)) {
		for (const operand of filter.operands) {
			const result = extractSentimentFromFilter(operand);
			if (result) return result;
		}
	}

	return undefined;
}

/**
 * Recursively removes sentiment filters from a filter tree.
 */
export function removeSentimentFromFilter(
	filter: Filter | null | undefined
): Filter | undefined {
	if (!filter) return undefined;

	if (filter.field === 'sentiment') {
		return undefined;
	}

	if (filter.operands && Array.isArray(filter.operands)) {
		const filteredOperands = filter.operands
			.map((op) => removeSentimentFromFilter(op))
			.filter((op): op is Filter => op !== undefined);

		return {
			...filter,
			operands: filteredOperands,
		};
	}

	return filter;
}

/**
 * Processes a catalog filter to extract sentiment and return the filter without sentiment.
 */
export function processSentimentFilter(catalogFilter: Filter | undefined): {
	sentimentFilter: 'positive' | 'negative' | undefined;
	filterWithoutSentiment: Filter | undefined;
} {
	let sentimentFilter: 'positive' | 'negative' | undefined;
	let filterWithoutSentiment = catalogFilter;

	if (catalogFilter && typeof catalogFilter === 'object') {
		const extractedSentiment = extractSentimentFromFilter(catalogFilter);
		if (
			extractedSentiment === 'positive' ||
			extractedSentiment === 'negative'
		) {
			sentimentFilter = extractedSentiment;
		}

		filterWithoutSentiment = removeSentimentFromFilter(catalogFilter);
	}

	return { sentimentFilter, filterWithoutSentiment };
}
